import { useDragDropContext } from "../../../utils/dragDropUtil";
import { isNil } from "lodash-es";
import DroppableList from "./DroppableList";
import type { Id } from "@thisbeyond/solid-dnd";
import type { VoidComponent } from "solid-js";
import { createEffect, createSignal } from "solid-js";
import { createMousePosition } from "@solid-primitives/mouse";
import { KEY } from "../../../utils/keyboardUtil";
import type { DraggableData } from "../../../components/field/draggable/Draggable";
import { useKeyDownEvent } from "@solid-primitives/keyboard";
import { useFormServices } from "../../../services/formServices";
import type { FieldServiceType } from "../../../services/FieldService";
import FieldId from "../../../fields/FieldId";
import type { FieldConfig } from "../../../fields/types";
import withErrorBoundary from "../../../utils/withErrorBoundary";
import FavouriteService from "../../../services/FavouriteService";
import SubformService from "../../../services/SubformService";
import { makeEventListener } from "@solid-primitives/event-listener";
import { useFormEditorSidePanel } from "../FormEditorContent";
import { fields } from "../../../fields/fields";
import FieldShowEvent from "../../../events/FieldShowEvent";
import SubformId from "../../../state/SubformId.ts";

const X_OFFSET_OVERLAY = 15;
const Y_OFFSET_OVERLAY = 20;
const MAX_SCROLL_SPEED = 200;
const SCROLL_ZONE_HEIGHT = 120;

const addFieldToList = (fieldService: FieldServiceType, field: FieldConfig, droppableId: Id) => {
  if (droppableId === "last") {
    fieldService.addFieldToEnd(field);
  } else {
    fieldService.addFieldBefore(field, FieldId.from(droppableId.toString()));
  }
};

const moveFieldInList = (fieldService: FieldServiceType, field: FieldConfig, droppableId: Id) => {
  if (droppableId === "last") {
    fieldService.moveFieldToEnd(field.uid);
  } else {
    fieldService.moveFieldBefore(field.uid, FieldId.from(droppableId.toString()));
  }
};

type EditorContentProps = {
  header?: { title: string; description?: string };
  contentContainer: HTMLDivElement;
};

const EditorContent: VoidComponent<EditorContentProps> = (props) => {
  const { fieldService, currentScope, ruleService } = useFormServices();
  const [, { onDragStart, onDragEnd, dragEnd, onDragMove, addTransformer, removeTransformer, recomputeLayouts }] =
    useDragDropContext();
  const [ignoredItems, setIgnoredItems] = createSignal<Id[]>([]);
  const mousePosition = createMousePosition(window);
  const [isCancelled, setCancelled] = createSignal(false);
  const [intervalId, setIntervalId] = createSignal<NodeJS.Timeout>();
  const { setSelectedPanel } = useFormEditorSidePanel();
  const keyDownEvent = useKeyDownEvent();

  const onDragCancel = (e: KeyboardEvent) => {
    e.preventDefault();
    if (e.key === KEY.Escape) {
      setCancelled(true);
      dragEnd();
    }
  };

  /**
   * We need to track the current and next element to ignore their respective dropzones.
   * As we're rendering a dropzone above an element, we need to hide the dropzone both above and below the current element.
   * Otherwise, you'd be moving the element to the same position, which looks silly.
   */
  onDragStart(({ draggable }) => {
    const currentItems = fieldService.getFields();
    const currentItemIndex = currentItems.findIndex((field) => field.uid.equals(draggable.id.toString()));
    if (currentItemIndex !== -1) {
      setIgnoredItems([
        draggable.id,
        ...(currentItemIndex < currentItems.length - 1
          ? [currentItems[currentItemIndex + 1].uid.toString()]
          : ["last"]),
      ]);
    }
    window.addEventListener("keydown", onDragCancel);

    const yDelta = mousePosition.y - draggable.layout.corners.topLeft.y;
    const xDelta = mousePosition.x - draggable.layout.corners.topLeft.x;

    addTransformer("draggables", draggable.id, {
      id: draggable.id,
      order: 0,
      callback: (transform) => ({
        x: transform.x + xDelta - X_OFFSET_OVERLAY,
        y: transform.y + yDelta - Y_OFFSET_OVERLAY,
      }),
    });
  });

  // oxlint-disable-next-line solid/reactivity
  onDragEnd(({ draggable, droppable }) => {
    clearInterval(intervalId());
    window.removeEventListener("keydown", onDragCancel);
    removeTransformer("draggables", draggable.id, draggable.id);

    if (isNil(droppable) || isCancelled()) {
      setCancelled(false);
      return;
    }

    const field = fieldService.getField(FieldId.from(draggable.id.toString()));
    if (field === undefined) {
      const data = draggable.data as DraggableData;

      if (isNil(data.favourite)) {
        addFieldToList(fieldService, fieldService.createField(data.type), droppable.id);
      } else {
        const domainObjects = FavouriteService.restore(data.favourite, currentScope);

        domainObjects.subforms.forEach(SubformService.addSubform);
        domainObjects.rules.forEach(ruleService.addToEndWithScope);
        domainObjects.fields.forEach((field) => {
          addFieldToList(fieldService, field, droppable.id);
        });
      }
    } else {
      moveFieldInList(fieldService, field, droppable.id);
    }

    setIgnoredItems([]);
  });

  // oxlint-disable-next-line solid/reactivity
  onDragMove(() => {
    const id = intervalId();
    clearInterval(id);
    setIntervalId(undefined);

    const rect = props.contentContainer.getBoundingClientRect();
    const relativeX = mousePosition.x - rect!.left;
    const relativeY = mousePosition.y - rect!.top;
    const xInside = relativeX > 0 && relativeX < rect!.width;

    if (xInside && relativeY > 0 && relativeY < SCROLL_ZONE_HEIGHT) {
      setIntervalId(
        setInterval(() => {
          props.contentContainer.scrollBy(0, -MAX_SCROLL_SPEED / relativeY);
        }),
      );
    } else if (xInside && relativeY > rect!.height - SCROLL_ZONE_HEIGHT && relativeY < rect!.height) {
      setIntervalId(
        setInterval(() => {
          props.contentContainer.scrollBy(0, MAX_SCROLL_SPEED / (rect!.height - relativeY));
        }),
      );
    } else if (!isNil(id)) {
      // Re-computation is necessary since the position of the dropzones relative to the screen have changed
      recomputeLayouts();
    }
  });

  createEffect(() => {
    const activeElement = document.activeElement;
    if (activeElement === null) {
      return;
    }

    if (keyDownEvent()?.code === "ArrowDown" && activeElement.hasAttribute("data-container-trigger")) {
      const field = activeElement
        .closest('[data-type="field"]')
        ?.nextElementSibling?.querySelector("[data-container-trigger]");
      (field as HTMLButtonElement)?.focus();
    }

    if (keyDownEvent()?.code === "ArrowUp" && activeElement.hasAttribute("data-container-trigger")) {
      const field = activeElement
        .closest('[data-type="field"]')
        ?.previousElementSibling?.querySelector("[data-container-trigger]");
      (field as HTMLButtonElement)?.focus();
    }
  });

  makeEventListener<Record<string, FieldShowEvent>>(document, FieldShowEvent.eventName, (event) => {
    const fieldConfig = fieldService.getField(event.fieldId);
    if (isNil(fieldConfig)) {
      return;
    }

    if (
      (fieldConfig.scope instanceof SubformId && fieldConfig.scope.equals(currentScope)) ||
      fieldConfig.scope === currentScope
    ) {
      const field = fields[fieldConfig.type];
      setSelectedPanel({ type: "field", field, fieldConfig });
    }
  });

  return (
    <DroppableList
      currentScope={currentScope}
      header={props.header}
      ignoredZones={ignoredItems()}
      elements={fieldService.getFields()}
      id="editor"
    />
  );
};

const EditorContentWithErrorBoundary = withErrorBoundary(EditorContent);

export default EditorContentWithErrorBoundary;
